import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

// Services
import { useUploadFileMutation } from '../../../../services/upload/uploadQueries';

// Hooks
import useToast from '../../Toast/useToast';

// Utils
import cn from '../../../../utils/cn';

// Styles
import styles from './FileField.module.css';

// Components
import Dropzone from '../../../atoms/Dropzone/Dropzone';

/**
 * FileField - Form file upload field that uploads on selection and stores API response name
 * @param {object} props
 * @param {string | null} props.value - Current value (file name from API or null)
 * @param {(value: string | null) => void} props.onChange - Change handler
 * @param {string} [props.uploadUrl] - Relative upload URL (from form config field.file.uploadUrl). Omitted = service default.
 * @param {string} [props.name] - Field name
 * @param {boolean} [props.disabled] - Whether input is disabled
 * @param {boolean} [props.readOnly] - Read-only (posted, not user-editable)
 * @param {string} [props.label] - Label when no file selected
 * @param {{message?: string}} [props.error] - Validation error (e.g. required on submit)
 * @param {string} [props.id] - Input id for accessibility
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} FileField component
 */
function FileField({
  value = null,
  onChange,
  uploadUrl,
  name,
  disabled = false,
  readOnly = false,
  label,
  error = null,
  id,
  className = '',
}) {
  const { t } = useTranslation();
  const { notify } = useToast();

  const defaultLabel = t('form.file.selectFile');
  const resolvedLabel = label ?? defaultLabel;

  const { mutate, isPending } = useUploadFileMutation(uploadUrl, {
    onSuccess: (data) => {
      onChange(data?.name ?? null);
    },
    onError: (err) => {
      notify({ message: err?.message ?? t('form.file.uploadError'), type: 'alert' });
      onChange(null);
    },
  });

  const displayLabel = value ?? (isPending ? t('form.file.uploading') : resolvedLabel);

  const onDrop = useCallback(
    (/** @type {File[]} */ acceptedFiles) => {
      if (acceptedFiles.length === 0) return;
      mutate(acceptedFiles[0]);
    },
    [mutate],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
  } = useDropzone({
    onDrop,
    disabled: disabled || readOnly || isPending,
    multiple: false,
  });

  return (
    <div className={cn([styles.wrapper, className])}>
      <Dropzone
        id={id || `file-dropzone-${name}`}
        label={displayLabel}
        disabled={disabled || readOnly || isPending}
        dropzoneProps={{
          getRootProps,
          getInputProps,
          isDragActive,
          isDragAccept,
        }}
      />

      {(error?.message) && (
        <div className={styles.error} role="alert">
          {error.message}
        </div>
      )}
    </div>
  );
}

export default FileField;
